/* eslint-disable max-len,@typescript-eslint/member-ordering */
import { Injectable, OnDestroy } from '@angular/core';
import {
  components,
  FoxitModule,
  FragmentComponentOptions_callback,
  getAddonPath,
  PDFUIOptions,
} from 'webcommon/lazy-load-commonjs/foxit';
import { FoxitLicenseDto, patchObjectFn } from 'webcommon/shared';
import { FoxitPdfRendererBase } from './FoxitPdfRendererBase';

// Usage: readonly viewer of pdf
// If we need to allow actual changes to the document that will be saved as attachments in PRM,
// then we should split out into another class other than this one.

type ToolbarButtonName = 'download-file-button' | 'print-button';

type ToolbarButtonComponent = components.Component<PointerEvent>;

@Injectable()
export class FoxitViewRenderer extends FoxitPdfRendererBase implements OnDestroy {
  override readonly pdfUI = this.createPDFUI();
  private _showDownloadButton = false;
  private _showPrintButton = false;

  /**
   * Supplies a minimal toolbar template instead of the default Foxit RibbonAppearance,
   * so we can precisely control which buttons and panels are exposed in a read-only viewer.
   * Anything omitted here simply doesn't exist in the DOM — no hide/disable dance needed.
   */
  protected override getNewTemplate(): PDFUIOptions['template'] {
    return `
<webpdf>
  <toolbar name="toolbar" class="fv__ui-toolbar-scrollable">
    <group-list name="home-toolbar-group-list">
      <group name="home-tab-group-hand" retain-count="4">
        <hand-button></hand-button>
        <xbutton
          @controller="states:SelectTextImageController"
          name="select-text-image"
          @tooltip tooltip-title="Select Text/Image"
          title="Select Text/Image"
          icon-class="fv__icon-toolbar-select-text-image"
        ></xbutton>
        <download-file-button></download-file-button>
        <print:print-button></print:print-button>
      </group>
      <group name="comment-tab-group-stamp" retain-count="2">
        <single-page-button></single-page-button>
        <continuous-page-button></continuous-page-button>
      </group>
      <group name="home-tab-group-nav" retain-count="5">
        <goto-first-page-button></goto-first-page-button>
        <goto-prev-page-button></goto-prev-page-button>
        <goto-page-input></goto-page-input>
        <goto-next-page-button></goto-next-page-button>
        <goto-last-page-button></goto-last-page-button>
      </group>
      <group name="home-tab-group-zoom">
        <zoom-out-button></zoom-out-button>
        <editable-zoom-dropdown></editable-zoom-dropdown>
        <zoom-in-button></zoom-in-button>
      </group>
    </group-list>
  </toolbar>
  <div class="fv__ui-body">
    <sidebar name="sidebar">
      <thumbnail-sidebar-panel></thumbnail-sidebar-panel>
      <search-sidebar-panel></search-sidebar-panel>
    </sidebar>
    <viewer>@touch-to-scroll</viewer>
  </div>
  <template name="template-container">
    <text-sel:text-selection-tooltip></text-sel:text-selection-tooltip>
    <print:print-dialog></print:print-dialog>
  </template>
</webpdf>
    `;
  }

  /**
   * Forces continuous-view-mode as the default regardless of what the license config specifies,
   * because single-page scrolling behaves inconsistently in a read-only context (see base class).
   */
  protected override createConfig(foxitSetup: FoxitLicenseDto): FoxitLicenseDto {
    const defaultConfig = super.createConfig(foxitSetup);
    return {
      ...defaultConfig,
      DefaultViewMode: 'continuous-view-mode',
    };
  }

  /**
   * Patches must be removed before the base class destroys the viewer;
   * leaving them in place would keep references alive on already-destroyed controller objects.
   */
  override async ngOnDestroy(): Promise<void> {
    this._downloadPatchTeardown?.();
    this._downloadPatchTeardown = undefined;
    this._printPatchTeardown?.();
    this._printPatchTeardown = undefined;
    await super.ngOnDestroy();
  }

  // try to download the file programmatically
  async tryDownloadFile(e: PointerEvent) {
    const root = await this.pdfUI.getRootComponent();
    // this is a little hacky, but it allows the normal download button behavior to be initiated
    // without actually clicking the download button
    await this.foxitModule.controllers.DownloadFileController.prototype.handle.call(root, e);
  }
  private _downloadPatchTeardown?: () => void;
  /**
   * Replaces the default DownloadFileController.handle with a caller-supplied function,
   * allowing the host to intercept the download and supply its own data source
   * (e.g. fetching a signed URL) rather than triggering Foxit's built-in download.
   * Calling this again replaces the previous override, so the teardown is invoked first.
   */
  async overrideDownloadButton(fn: FragmentComponentOptions_callback<PointerEvent>): Promise<void> {
    this._downloadPatchTeardown?.();
    // default controller: UIExtension.controllers.DownloadFileController
    const controller = (await this.getComponentOrThrow<components.widgets.ButtonComponent>('download-file-button')).controller;
    this._downloadPatchTeardown = patchObjectFn(controller, 'handle', (_originalFn) => {
      return fn;
    });
  }

  // try to print the file programmatically
  async tryPrintFile(_e: PointerEvent) {
    // This currently uses reasonable defaults to print, instead of bringing up the foxit print dialog.
    // This is mainly because it's one less click, and I think they would rarely change the pages or the quality.
    // Idk, we probably change this behavior again in the future.
    // there's also => this.pdfUI.callAddonAPI('PrintUIXAddon', 'showPrintDialog', [])
    //
    // more notes about printing:
    // 	- in the current version, it seems like the printed pdf always has the title of the html page, and doesn't preserve the title from the pdf metadata, but in the v10 demo on https://webviewer-demo.foxit.com/, it doesn't do that;
    // 	I mean maybe this doesn't matter that much, because normally they are actually printing it, not just printing to pdf
    // 	- the printing doesn't appear to always handle portrait, landscape that well (try printing with http://localhost:8080/examples/UIExtension/complete_webViewer/),
    // 	but it seems like it may have been fixed in later versions:	https://webviewer-demo.foxit.com/
    //  - in later versions, it seems like they changed some of how the printing is affected by quality,
    //  because quality 100 looks different in the demo app on their website as compared to our app with 7.6
    const pageCount = await this.getPageCount();
    if (!pageCount) {
      return;
    }
    await this.pdfUI.print({
      // create array with all page indexes
      pages: Array.from({ length: pageCount }, (_, i) => i),
      printType: ['page', 'annot'],
      quality: 100,
      showHeaderFooter: false,
    });
  }
  private _printPatchTeardown?: () => void;
  /**
   * Replaces the default ShowPrintDialogController.handle so callers can skip the Foxit
   * print dialog entirely and drive printing programmatically (or block it altogether).
   * Calling this again replaces the previous override, so the teardown is invoked first.
   */
  async overridePrintButton(fn: FragmentComponentOptions_callback<PointerEvent>): Promise<void> {
    this._printPatchTeardown?.();
    // if you want the default behavior,
    // you need <print:print-dialog></print:print-dialog> in the <template> tag
    // default controller: print:ShowPrintDialogController
    const controller = (await this.getComponentOrThrow<components.widgets.ButtonComponent>('print-button')).controller;
    this._printPatchTeardown = patchObjectFn(controller, 'handle', (_originalFn) => {
      return fn;
    });
  }

  /**
   * Persists visibility intent in private state before the viewer is mounted,
   * then applies it once mounting is confirmed — callers can safely invoke this
   * before or after the viewer finishes initializing.
   */
  async setToolbarButtonVisibility(options: {
    showDownloadButton?: boolean;
    showPrintButton?: boolean;
  }): Promise<void> {
    if (options.showDownloadButton !== undefined) {
      this._showDownloadButton = options.showDownloadButton;
    }
    if (options.showPrintButton !== undefined) {
      this._showPrintButton = options.showPrintButton;
    }

    await this.getAfterMountedP();
    await this.updateToolbarButtonVisibility('download-file-button', this._showDownloadButton);
    await this.updateToolbarButtonVisibility('print-button', this._showPrintButton);
  }

  async setSidebarOpen(isOpen: boolean): Promise<void> {
    const root = await this.getAfterMountedP();
    const sidebar = root.getComponentByName<components.widgets.SidebarComponent>('sidebar');
    if (!sidebar) {
      return;
    }

    if (isOpen) {
      sidebar.expand();
      return;
    }

    sidebar.collapse();
  }

  /**
   * Foxit's show()/hide() and enable()/disable() alone are not always sufficient to
   * visually remove a button, because some Foxit versions re-show components after
   * certain events. The 'fv__ui-force-hide' CSS class acts as a reliable override
   * that survives those resets. Both the component API and the class are applied
   * together to cover all Foxit versions in use.
   */
  private async updateToolbarButtonVisibility(name: ToolbarButtonName, isVisible: boolean): Promise<void> {
    const component = await this.pdfUI.getComponentByName<ToolbarButtonComponent>(name);
    if (!component) {
      return;
    }

    // todo: there's also remove(), if we won't want to show it again?
    if (isVisible) {
      component.show();
      component.enable();
      component.element.classList.remove('fv__ui-force-hide');
    } else {
      component.hide();
      component.disable();
      component.element.classList.add('fv__ui-force-hide');
    }
  }

  /**
   * Extends the base options with read-only-specific configuration:
   * - Loads only the print and thumbnail addons (all annotation/editing addons are excluded).
   * - Strips annotation tools from the text-selection tooltip and thumbnail context menu.
   * - Hides the thumbnail delete item via CSS rather than removing it, because Foxit's
   *   ThumbSidebarPanelController throws if that component is absent from the menu.
   * - Overrides doc permissions via getDocPermissions to prevent drag-and-drop reordering
   *   and context menu editing actions at the permission level, which is more robust than
   *   individually disabling each UI element.
   */
  protected override getOptions(
    foxitModule: FoxitModule,
    foxitSetup: FoxitLicenseDto,
    element: HTMLElement,
  ): PDFUIOptions {
    const defaultOptions = super.getOptions(foxitModule, foxitSetup, element);
    defaultOptions.fragments = [
      {
        target: 'sidebar',
        config: {
          attrs: {
            // default sidebar to open
            open: false,
          },
        },
      },
      {
        target: 'sidebar-thumbnail-panel',
        config: {
          // default thumbnail panel to the active item in the sidebar
          active: true,
          attrs: {
            // Set the tooltip-title
            '@tooltip': 'Page Thumnails',
            'tooltip-title': 'Thumbnails',
            'tooltip-placement': 'right',
            title: 'Thumbnails'
            // Set the panel title
            // look at "The sidebar-panel properties" in the docs for info on this
            // OR SidebarPanelComponentOptions Interface Reference
          },
        },
      },
      {
        target: 'fv--text-selection-tooltip>fv--text-selection-tooltip-create-highlight',
        // remove from text-selection menu
        action: foxitModule.UIConsts.FRAGMENT_ACTION.REMOVE,
      },
      {
        target: 'fv--text-selection-tooltip>fv--text-selection-tooltip-create-strikeout',
        // remove from text-selection menu
        action: foxitModule.UIConsts.FRAGMENT_ACTION.REMOVE,
      },
      {
        target: 'fv--text-selection-tooltip>fv--text-selection-tooltip-create-underline',
        // remove from text-selection menu
        action: foxitModule.UIConsts.FRAGMENT_ACTION.REMOVE,
      },
      {
        target: 'fv--text-selection-tooltip>fv--text-selection-tooltip-create-bookmark',
        // remove from text-selection menu
        action: foxitModule.UIConsts.FRAGMENT_ACTION.REMOVE,
      },
      {
        target: 'fv--thumbnail-contextmenu>contextmenu-item-thumbnail-add-blank-page',
        // remove from thumbnail menu;
        // we could also just replace this whole context menu with specific items
        action: foxitModule.UIConsts.FRAGMENT_ACTION.REMOVE,
      },
      {
        target: 'fv--thumbnail-contextmenu>@contextmenu-separator',
        // remove from thumbnail menu
        action: foxitModule.UIConsts.FRAGMENT_ACTION.REMOVE,
      },
      {
        target: 'fv--thumbnail-contextmenu>contextmenu-item-thumbnail-delete',
        // I would rather remove this instead of hiding it.
        // However, the controller for thumbnail-sidebar-panel expects there to be a component matching this in the menu,
        // or it will throw TypeError: Cannot read properties of null (reading 'previousSiblings')
        // when trying to open the context menu.
        config: {
          cls: 'fv__ui-force-hide'
        }
      },
    ];
    defaultOptions.addons = [
      getAddonPath('print'),
      getAddonPath('thumbnail'),
    ];
    defaultOptions.viewerOptions.defaultScale = 0.5;
    defaultOptions.viewerOptions.customs = {
      /* eslint-disable no-bitwise */
      // set permissions to disable thumbnail drag-and-drop, and thumbnail contextmenu
      getDocPermissions: () =>
        foxitModule.PDFViewCtrl.PDF.constant.User_Permissions.printHigh
        | foxitModule.PDFViewCtrl.PDF.constant.User_Permissions.print
        | foxitModule.PDFViewCtrl.PDF.constant.User_Permissions.extract
        | foxitModule.PDFViewCtrl.PDF.constant.User_Permissions.extractAccess
        | foxitModule.PDFViewCtrl.PDF.constant.User_Permissions.assemble
      /* eslint-enable no-bitwise */
    };
    return defaultOptions;
  }
}


// this is from FoxitPDFSDKforWeb_DeveloperGuide.pdf:
//
// const exampleTemplate = `
//   <!-- Layout template must take <webpdf> as the root component. -->
//   <webpdf>
//     <!-- Layout templates support all html tags and properties. -->
//     <div class="toolbar" style="display:flex;flex-direction:row;padding:6px">
//       <!-- The colon prefix is the name of the component module, and the component name and module name
//       must be in lowercase. -->
//       <print:print-button></print:print-button>
//       <!-- The parameters that begin with @ are used to mark directives, the content that follow with @ is the
//       name of directives. If the directive is registered in a non-root module, then the directive name should be written
//       in the @module-name:directive-name format. -->
//
//       <xbutton name="create-typewriter-button" @controller="states:CreateTypewriterController" iconclass="fv__icon-toolbar-typewriter">toolbar.create.typewriter</xbutton>
//     </div>
//     <div class="fv__ui-body">
//       <!-- Viewer is used to display the area of PDF, so there must be a viewer component in the layout template -->
//       <viewer @touch-to-scroll></viewer>
//     </div>
//     <!-- Template is a re-written html template tag. It is typically used to hold the components that do not need to
//     be displayed immediately, such as dialog boxes, right-click menus, floating boxes, and etc.. -->
//     <template name="template-container">
//       <print:print-dialog></print:print-dialog>
//     </template>
//   </webpdf>
// `;


// default for sidebar-thumbnail-panel
// <thumbnail:sidebar-thumbnail name="sidebar-thumbnail-panel"/>


// I think that selecting multiple thumbnails for rotation (or other actions) was introduced in 8.5.

// default for sidebar-thumbnail
// <sidebar-panel name="sidebar-thumbnail" title="thumbnail:title"  class="fv__sidebar-thumbnail" icon-class="fv__icon-sidebar-thumbnail">
// 	<thumbnail:thumbnail-panel name="fv-thumbnail-panel" @controller="thumbnail:ThumbSidebarPanelController">
// 		<template>
// 			<contextmenu name="fv--thumbnail-contextmenu">
// 				<contextmenu-item @controller="thumbnail:RotateLeftController" name="contextmenu-item-thumbnail-rotate-left">thumbnail:contextmenu.rotateLeft</contextmenu-item>
// 				<contextmenu-item @controller="thumbnail:RotateRightController" name="contextmenu-item-thumbnail-rotate-right">thumbnail:contextmenu.rotateRight</contextmenu-item>
// 				<contextmenu-separator></contextmenu-separator>
// 				<contextmenu-item @controller="thumbnail:InsertBlankPageController" name="contextmenu-item-thumbnail-add-blank-page">thumbnail:contextmenu.addBlankPage</contextmenu-item>
// 				<contextmenu-separator></contextmenu-separator>
// 				<contextmenu-item @controller="thumbnail:DeletePageController" name="contextmenu-item-thumbnail-delete">thumbnail:contextmenu.delete</contextmenu-item>
// 			</contextmenu>
// 		</template>
// 	</thumbnail:thumbnail-panel>
// </sidebar-panel>


// Changing doc permissions seems to be the more standard way to control the behavior of buttons or panels across the board,
// which applies to the need to disable the drag-and-drop, and contextmenu for the thumbnail-panel.
//
// But, here are some alternatives that I came up with before realizing this:
// For context menu stuff, search for => 'Hiding the right-click menu or items' in Developer Guide
// => fragments => cls: "fv__ui-force-hide", or override createContextMenu
//
// If we want to disable dragging in the Thumbnail sidebar, then 1 way would be to
// replace the controller of 'fv-thumbnail-panel' with a subclass of the ThumbSidebarPanelController, and override mounted(),
// so that after that call, we can set this.enableDragAndDrop(0)
// and then return the result of the call.
// Also, we would have to patch 'enableDragAndDrop', so that it doesn't get called, and re-enabled later, which can happen from a handler for renderFileSuccess.
// OR
// use getComponentByName('fv-thumbnail-panel') => the result would already be mounted => pass to lockToReadonly()
// OR
// Contacting Foxit support may be necessary, since some of this seems hacky

// return teardown fn
// function lockToReadonly(component: ThumbnailPanelComponent) {
//   component.enableDragAndDrop(0);
//   return patchObjectFn(component, 'enableDragAndDrop', () => {
//     return () => {
//       // noop
//     };
//   });
// }
